// 花嫁「返しの一閃」(veilSweepLink) の間の絵と振りの跡 (2026-10-23 に比較版・10-27 に both を配布版の既定にした)。
//
// 10-27 検収 kensyu-bride-1023b の答え K-1「両方」・K-2「選んだ版を配布版に」→ 間の絵＋振りの跡 (both) が配布版。
//   開発版だけ ?brideKaeshi=old で前の配布版 (キーポーズ3枚だけ・跡なし)、tween・trail で片方だけ。
//
// 作者の検収 (kensyu-bride-kaeshi-blade-1023・10-23)：
//   B-1「配布版をいじっていくみたいな風に考えてほしい…今はキーポーズしかないような状態で、間の絵とかがあまりにも少なくて、
//        飛んでるように、絵が飛んで見えるんだよね。…これはエフェクトが付いてないからそう見えるのか、ちょっとわからない。」
//   B-2 カクつく所がある／B-3 直してからもう一度。刀の版 (?brideKaeshi=blade) はやめる。
// 作り (技の数値・当たり・弾く回数は配布版のまま。変えるのは描く物だけ)：
//   ?brideKaeshi=tween ＝ 配布版のキーポーズ3枚の間に、Codex が同じ絵から描いた間の絵3枚を挟む (FX なし)。
//     膝立ち side_crouch → 立ち上がりかけ・ヴェールが頭上へ回る kaeshi_tween_rise_1023 → ほぼ立って腕を伸ばしかける kaeshi_tween_reach_1023
//     → 振り抜き veil_sweep_contact → 上体を起こしかける kaeshi_tween_settle_1023 → 立ち姿 veil_follow。
//   ?brideKaeshi=trail ＝ 絵は配布版のまま、ヴェールの縁が通った弧に振りの跡 (刀の版の軌跡と同じ描き方) を出すだけ。
//   ?brideKaeshi=both ＝ 両方。
//   赤・朱・マゼンタは使わない (赤＝弾けない攻撃の専用色)。跡の色は刀の版と同じ冷たい銀と藤色。
// 配布版は URL を読まない (開発版でなければ常に both)。
//   ⚠️ 識別子に URL のキーの綴りを入れない (scripts/check_dev_leak.mjs)。
// 記録 = docs/research/2026-10-23-bride-kaeshi-tween.md

import { BOSS_ATTACK_TIMELINES, BRIDE_MOTION_TIMELINE_VARIANTS, getBrideMotionArt, type BossAttackTimeline } from './boss_pose_timeline'
import { drawTrail, type Anchor, type ArcShape } from './bride_kaeshi_blade'
import type { Boss } from './types'

// 溜め 350ms の 0.70 (当たりの 105ms 前) から立ち上がりかけ、0.86 (49ms 前＝3コマ) で腕を伸ばしかける。
// 隙 650ms は 0.22 (143ms) まで振り抜いたまま止め、0.40 まで起こしかけ、配布版と同じ 0.40 で立ち姿へ。
export const BRIDE_KAESHI_TWEEN_TIMELINE: BossAttackTimeline = [
  { phase: 'telegraph', at: 0.0, image: 'side_crouch', hold: true, tag: 'anticipation' },
  { phase: 'telegraph', at: 0.70, image: 'kaeshi_tween_rise_1023', hold: true },
  { phase: 'telegraph', at: 0.86, image: 'kaeshi_tween_reach_1023', hold: true },
  { phase: 'active', at: 0.0, image: 'veil_sweep_contact', hold: true, tag: 'contact' },
  { phase: 'recovery', at: 0.0, image: 'veil_sweep_contact', hold: true, tag: 'followThrough' },
  { phase: 'recovery', at: 0.22, image: 'kaeshi_tween_settle_1023', hold: true },
  { phase: 'recovery', at: 0.40, image: 'veil_follow', tag: 'followThrough' },
]

export type BrideKaeshiTweenMode = 'tween' | 'trail' | 'both' | 'none'
let tween = true
let trail = true
export function brideKaeshiTweenOn(): boolean { return tween }
export function brideKaeshiTrailOn(): boolean { return trail }
// bride_kaeshi.ts の setBrideKaeshi から呼ぶ (setBrideMotionArt・setBrideIssen の後)。
// 開発版でなければ常に both (配布版)。none は開発版の ?brideKaeshi=old と、ほかの比較版 (new・slow・blade) の間。
export function setBrideKaeshiTween(mode: BrideKaeshiTweenMode): void {
  const m: BrideKaeshiTweenMode = import.meta.env.DEV ? mode : 'both'
  tween = m === 'tween' || m === 'both'
  trail = m === 'trail' || m === 'both'
  const tl = BOSS_ATTACK_TIMELINES['bride']!
  if (tween) tl.veilSweepLink = BRIDE_KAESHI_TWEEN_TIMELINE
  else if (tl.veilSweepLink === BRIDE_KAESHI_TWEEN_TIMELINE) tl.veilSweepLink = BRIDE_MOTION_TIMELINE_VARIANTS[getBrideMotionArt()].veilSweepLink
}
// 読み込んだ時点で配布版の並びにする (setBrideKaeshi を呼ばない入口でも間の絵が入るように)。
setBrideKaeshiTween('both')

// ───────── 振りの跡 (ボスの足元から・前＝+x・上＝+y) ─────────
/**
 * ヴェールの縁が通る弧。肩を中心に、背中側の頭上から前へ回って、振り抜いた絵の裂けた帯の先 (肩の高さより少し下) で止まる。
 * 振り抜いた絵 veil_sweep_contact は boss.x より 90 ほど背中側に体があるので、中心も背中側に置く (10-23 の撮影で合わせた)。
 */
export const VEIL_SWEEP_ARC: ArcShape = { center: { fwd: -54, up: 112 }, radius: 142, fromDeg: 105, toDeg: -10, headW: 14 }
/** 軌跡の長さ (弧の何割ぶん後ろまで見えるか)。 */
const TRAIL_LEN = 0.8
/** 当たりが終わってから消えるまで (秒)。 */
export const VEIL_TRAIL_FADE_S = 0.16
/** 当たりの頭から軌跡の先が弧の終わりへ着くまで (ms)。振り抜いた絵は当たりの頭で既に出ているので短くする。 */
export const VEIL_HEAD_REACH_MS = 50
/** 間の絵がある時 (both) だけ、腕を伸ばしかける絵 (当たりの 49ms 前) から走り出す。 */
export const VEIL_SWING_LEAD_MS = 49
const HEAD_AT_CONTACT_WITH_LEAD = 0.45

let anchor: Anchor | null = null
let fadeFrom = -1
let wasActive = false
let lastTime = -1

/** 毎フレーム描く (ボスの絵の後)。 */
export function drawBrideKaeshiTrail(ctx: CanvasRenderingContext2D, boss: Boss | null | undefined, time: number, footY: number): void {
  if (!trail || !boss || boss.id !== 'bride') return
  if (time < lastTime) { anchor = null; fadeFrom = -1; wasActive = false }
  lastTime = time
  const dir = boss.facing === 'right' ? 1 : -1
  const atk = boss.currentAttack
  const active = boss.state === 'active' && atk?.id === 'veilSweepLink'
  const lead = tween ? VEIL_SWING_LEAD_MS : 0
  const startHead = tween ? HEAD_AT_CONTACT_WITH_LEAD : 0
  const swinging = lead > 0 && boss.state === 'telegraph' && atk?.id === 'veilSweepLink'
    && boss.stateElapsed * 1000 >= atk.chargeMs - lead
  if (swinging) {
    anchor = { x: boss.x, footY, dir }
    const head = startHead * Math.min(1, (boss.stateElapsed * 1000 - (atk.chargeMs - lead)) / lead)
    drawTrail(ctx, anchor, head - TRAIL_LEN, head, 1, VEIL_SWEEP_ARC)
  } else if (active) {
    anchor = { x: boss.x, footY, dir }
    const r = Math.min(1, boss.stateElapsed * 1000 / VEIL_HEAD_REACH_MS)
    const head = startHead + (1 - startHead) * (1 - Math.pow(1 - r, 2))
    drawTrail(ctx, anchor, head - TRAIL_LEN, head, 1, VEIL_SWEEP_ARC)
    fadeFrom = -1
  } else if (wasActive && anchor) {
    fadeFrom = time
  }
  wasActive = active
  if (!active && anchor && fadeFrom >= 0) {
    const f = (time - fadeFrom) / VEIL_TRAIL_FADE_S
    if (f >= 1) { anchor = null; fadeFrom = -1 } else drawTrail(ctx, anchor, 1 - TRAIL_LEN * (1 - f), 1, 1 - f, VEIL_SWEEP_ARC)
  }
}

export function resetBrideKaeshiTrailForTest(): void {
  anchor = null; fadeFrom = -1; wasActive = false; lastTime = -1
}
